import React, { useCallback, useEffect, useState } from 'react';
import { ShieldCheck, Loader2, Check } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import { getDriverInsurance, acceptInsuranceDeclaration } from '../services/api';

const DECLARATION_TEXT =
  'I confirm that I hold, and will maintain, all insurance required by law to carry out paid delivery work in Ireland. ' +
  'I understand that I am solely responsible for ensuring my insurance remains valid and appropriate for the work I undertake ' +
  'through JustDeliver. I also understand that JustDeliver may request proof of insurance at any time, and failure to provide ' +
  'it may result in my account being suspended or terminated.';

/**
 * Driver insurance — a single self-declaration tickbox. No certificate
 * upload, no expiry, no admin review.
 */
export default function InsurancePanel({ onChange }) {
  const [state, setState] = useState(null);
  const [loading, setLoading] = useState(true);
  const [agreed, setAgreed] = useState(false);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    try {
      const { data } = await getDriverInsurance();
      setState(data);
      onChange?.(data);
    } catch (_e) {
      setState({ declared: false });
    } finally {
      setLoading(false);
    }
  }, [onChange]);

  useEffect(() => { load(); }, [load]);

  const submit = async () => {
    if (!agreed) return;
    setSaving(true);
    try {
      const { data } = await acceptInsuranceDeclaration();
      setState(data);
      onChange?.(data);
      toast.success('Insurance declared — you can place bids.');
      window.dispatchEvent(new Event('jd:profile-updated'));
    } catch (err) {
      toast.error(err?.response?.data?.detail || 'Could not save your declaration');
    } finally {
      setSaving(false);
    }
  };

  const declared = !!state?.declared;

  return (
    <Card className="rounded-xl border border-slate-200/80 shadow-sm" data-testid="insurance-panel">
      <CardHeader className="pb-3">
        <CardTitle className="text-base font-semibold flex items-center gap-2">
          <ShieldCheck className={`w-5 h-5 ${declared ? 'text-[#2E7D32]' : 'text-indigo-700'}`} strokeWidth={2} />
          Insurance Declaration
        </CardTitle>
      </CardHeader>
      <CardContent>
        {loading ? (
          <div className="flex items-center gap-2 text-sm text-gray-500 py-2">
            <Loader2 className="w-4 h-4 animate-spin" /> Loading…
          </div>
        ) : declared ? (
          <div className="flex items-start gap-3 p-3 rounded-sm border-2 border-emerald-200 bg-emerald-50" data-testid="insurance-declared-state">
            <div className="w-8 h-8 rounded-full bg-emerald-200 flex items-center justify-center flex-shrink-0">
              <Check className="w-4 h-4 text-emerald-800" strokeWidth={3} />
            </div>
            <div className="min-w-0">
              <p className="text-sm font-bold text-emerald-900">Insurance declared</p>
              <p className="text-xs text-emerald-800 mt-0.5">
                You confirmed you hold valid insurance for the deliveries you carry out
                {state?.declared_at ? ` on ${new Date(state.declared_at).toLocaleDateString('en-IE')}` : ''}.
                Customers see an “Insurance Declared” badge on your bids.
              </p>
              <p className="text-[11px] text-emerald-700/90 mt-2">
                Keep your cover valid and appropriate for the work you take on. JustDeliver may ask you for proof at any time.
              </p>
            </div>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="p-3 bg-indigo-50/50 border border-indigo-100 rounded-sm text-xs text-gray-800 leading-relaxed">
              {DECLARATION_TEXT}
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="insurance-declare"
                checked={agreed}
                onCheckedChange={(v) => setAgreed(!!v)}
                data-testid="insurance-declare-checkbox"
              />
              <Label htmlFor="insurance-declare" className="text-sm font-medium cursor-pointer leading-snug">
                I confirm I hold valid insurance for the deliveries I carry out
              </Label>
            </div>
            <Button
              onClick={submit}
              disabled={!agreed || saving}
              className="bg-[#2E7D32] text-white hover:bg-[#1B5E20] rounded-sm gap-2 h-10"
              data-testid="insurance-declare-submit"
            >
              {saving ? <><Loader2 className="w-4 h-4 animate-spin" /> Saving…</> : 'Save declaration'}
            </Button>
            <p className="text-[11px] text-gray-500">
              You need this declaration on file before you can place bids. No documents or photos required.
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
